import jwtAxios from '@tenant/services/auth/jwt-auth';

// Wave 1 (model/CRUD) + Wave 2 (run engine) API surface — shipped, unchanged by this
// FE-only slice. Wire format is PascalCase (Program.cs: PropertyNamingPolicy = null).
export const PIPELINE_APIS = {
  ROOT: 'api/pipelines',
};

// GET /api/pipelines -> List<PipelineGroupDto>{ AppInstanceId, Pipelines: PipelineDto[] }.
// No query params are exposed by the shipped endpoint (verified against BE source at
// /implement) — filtering is client-side (OQ-6 default).
export const getListPipelinesQuery = () => {
  return jwtAxios.get(PIPELINE_APIS.ROOT);
};

// GET /api/pipelines/{id} -> PipelineDto | 404.
export const getDetailPipelineQuery = (id) => {
  return jwtAxios.get(`${PIPELINE_APIS.ROOT}/${id}`);
};

// GET /api/pipelines/{id}/build-source -> PipelineBuildSourceDto | 404.
// The resolved LATEST build coordinates (Branch / Dockerfile / Repo / Tag) for the
// pipeline's ImageName — the same source the next run builds from — exposed under
// PIPELINE_READ (no REPOSITORY_READ needed). `Resolved: false` => no build source yet.
export const getPipelineBuildSourceQuery = (id) => {
  return jwtAxios.get(`${PIPELINE_APIS.ROOT}/${id}/build-source`);
};

// POST /api/pipelines — CreatePipelineDto{ Name, ImageName, int ContainerAppId }.
// 1:1 conflict -> 400 (RV-3, global middleware hard-codes 400, not 409).
export const createPipelineQuery = (payload) => {
  return jwtAxios.post(PIPELINE_APIS.ROOT, payload);
};

// PATCH /api/pipelines/{id} — UpdatePipelineDto{ Name?, ImageName?, ContainerAppId? }.
// Partial/nullable — only send changed fields (RV-4, NOT PUT).
export const updatePipelineQuery = ({ id, ...payload }) => {
  return jwtAxios.patch(`${PIPELINE_APIS.ROOT}/${id}`, payload);
};

// DELETE /api/pipelines/{id} (soft-delete).
export const deletePipelineQuery = (id) => {
  return jwtAxios.delete(`${PIPELINE_APIS.ROOT}/${id}`);
};

// POST /api/pipelines/{id}/run — PipelineRunTriggerDto{ AccessToken, BranchName? } (the git PAT,
// request-scoped, never persisted server- or client-side — RV-5 / NFR-Security). `BranchName` is
// the deploy-pipelines-run-overrides (Wave 4) ONE-OFF override (AC-2/AC-18/AC-19/AC-20) — omitted
// entirely (never sent as an empty string) when the caller did not choose to override, so the BE
// never has to distinguish "no override" from "an override of ''".
// Returns PipelineRunDto in Building; errors 404 / 400 (no prior build, OR an invalid override
// branch naming it — AC-18) / 409 (active run or build-in-progress race — the ONLY endpoint in
// this surface that returns 409).
export const runPipelineQuery = ({ id, AccessToken, BranchName }) => {
  const payload = { AccessToken };
  if (BranchName) {
    payload.BranchName = BranchName;
  }
  return jwtAxios.post(`${PIPELINE_APIS.ROOT}/${id}/run`, payload);
};

// POST /api/pipelines/{id}/branches — PipelineBranchListRequestDto{ AccessToken } (deploy-
// pipelines-run-overrides, Wave 4, AC-11/AC-12/AC-13/AC-14/AC-15/AC-16). The run dialog's branch
// picker, populated ONLY when the caller explicitly asks for it (never on page load, AC-24) —
// this function issues exactly the one request the caller asks for; it owns no cache, no poller,
// no query key of its own. POST because the PAT rides in the BODY (never a query string/path
// segment — access logs, browser history, proxy telemetry). Gated PIPELINE_READ, not PIPELINE_RUN
// or REPOSITORY_READ: it reads, it triggers nothing, and the caller's OWN PAT is what reaches the
// provider (NFR-Security).
// Returns PipelineBranchListDto{ Available, Reason, Branches, PageLimit, EffectiveBranch }.
// `Available: false` is a 200 (AC-14 "listing unavailable" — not an error); an invalid PAT is a
// 400 whose body never echoes the token (AC-15).
export const getPipelineBranchesQuery = ({ id, AccessToken }) => {
  return jwtAxios.post(`${PIPELINE_APIS.ROOT}/${id}/branches`, { AccessToken });
};

// GET /api/pipelines/{id}/runs -> List<PipelineRunDto>, newest-first.
export const getListPipelineRunsQuery = (id) => {
  return jwtAxios.get(`${PIPELINE_APIS.ROOT}/${id}/runs`);
};

// GET /api/pipelines/{id}/runs/{runId} -> PipelineRunDto | 404 (also 404 if the run
// isn't a child of the pipeline).
export const getPipelineRunQuery = ({ pipelineId, runId }) => {
  return jwtAxios.get(`${PIPELINE_APIS.ROOT}/${pipelineId}/runs/${runId}`);
};

// POST /api/pipelines/{id}/runs/{runId}/cancel — deploy-pipelines-run-cancel (Wave 5,
// AC-16/AC-20). No request body — no PAT, no credential of any kind: unlike POST .../run
// above, cancel needs none, so it sends none. Returns the updated PipelineRunDto (same shape
// as the trigger's) on 200; 409 { message } when the run is no longer cancellable (already
// terminal, or it reached Deploying); 404 for an unknown pipeline/run pairing (mirrors
// getPipelineRunQuery's own pairing check).
export const cancelPipelineRunQuery = ({ id, runId }) => {
  return jwtAxios.post(`${PIPELINE_APIS.ROOT}/${id}/runs/${runId}/cancel`);
};

// GET /api/pipelines/{id}/runs/{runId}/log -> PipelineRunLogDto | 404 (404 on the same
// terms as getPipelineRunQuery above: pipeline or run missing, not a child, soft-deleted).
//
// Gated PIPELINE_READ, NOT REPOSITORY_READ (deploy-pipelines-run-logs OQ-6) — this is the
// whole point of the endpoint existing: the detail page can show the build log without the
// viewer holding the Docker-Images permission, exactly as /build-source does for the build
// coordinates. `GET /api/docker-repositories/{id}/logs` (REPOSITORY_READ, useGetLogRepository,
// ViewLog.js) is untouched and keeps its own gate.
//
// 200 always carries a resolved state rather than making the caller infer from nulls (OQ-4):
//   Available=false + Reason="NotBuilt"          -> the run never started a build.
//   Available=false + Reason="SourceUnavailable" -> the build record is gone/soft-deleted.
//   Available=true  + Logs=""                    -> the source exists but wrote nothing.
//   Available=true  + Logs="…"                   -> the END of the build output, server-side
//                                                   bounded to the last 200 lines / 32 768
//                                                   characters; `Truncated` + `TotalLength`
//                                                   say so honestly.
// `Reason` is a closed set on the wire, but callers must still keep a defined fallback for an
// unrecognised value (resolveRunLogMessage does).
export const getPipelineRunLogQuery = ({ pipelineId, runId }) => {
  return jwtAxios.get(`${PIPELINE_APIS.ROOT}/${pipelineId}/runs/${runId}/log`);
};
